// src/pages/finance-portal/expense-reports/ExpenseReportDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Card, Chip, Alert, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
} from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#64748b' },
    SUBMITTED: { bg: '#dbeafe', color: '#1e40af' },
    APPROVED: { bg: '#dcfce7', color: '#166534' },
    REJECTED: { bg: '#fecaca', color: '#991b1b' },
    REIMBURSED: { bg: '#ede9fe', color: '#6d28d9' },
    VOID: { bg: '#f1f5f9', color: '#475569' },
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ExpenseReportDetail() {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Reject dialog
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const fetchReport = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/expense-reports/${reportId}`);
            if (res.data.success) {
                setReport(res.data.data);
            } else {
                setError(res.data.message || 'Failed to load report');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load report');
        } finally {
            setIsLoading(false);
        }
    }, [reportId]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const handleSubmit = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.post(`/api/finance/expense-reports/${reportId}/submit`, {});
            if (res.data.success) {
                fetchReport();
            } else {
                setError(res.data.message || 'Failed to submit report');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to submit report');
        } finally {
            setActionLoading(false);
        }
    };

    const handleApprove = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.post(`/api/finance/expense-reports/${reportId}/approve`, {});
            if (res.data.success) {
                fetchReport();
            } else {
                setError(res.data.message || 'Failed to approve report');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to approve report');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.post(`/api/finance/expense-reports/${reportId}/reject`, {
                reason: rejectReason,
            });
            if (res.data.success) {
                setRejectOpen(false);
                setRejectReason('');
                fetchReport();
            } else {
                setError(res.data.message || 'Failed to reject report');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reject report');
        } finally {
            setActionLoading(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 4 }}>
                <Skeleton height={60} width={300} sx={{ mb: 2 }} />
                <Skeleton height={200} sx={{ borderRadius: '16px', mb: 2 }} />
                <Skeleton height={300} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }

    const statusStyle = STATUS_COLORS[report?.status] || STATUS_COLORS.DRAFT;
    const expenses = report?.expenses || [];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/finance-portal/expense-reports')}
                        sx={{ fontFamily, textTransform: 'none', color: 'rgba(255,255,255,0.8)', mb: 1, '&:hover': { color: '#fff' } }}
                    >
                        Back to Expense Reports
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <SummarizeIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                        {report?.title || 'Expense Report'}
                                    </Typography>
                                    <Chip
                                        label={report?.status?.replace(/_/g, ' ') || 'DRAFT'}
                                        size="small"
                                        sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, height: 24, bgcolor: 'rgba(255,255,255,0.25)', color: '#fff' }}
                                    />
                                </Box>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    {report?.report_number || ''}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                            {formatCurrency(report?.total_amount)}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Info Section */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Report Number</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 600, color: '#1e293b', mt: 0.5 }}>{report?.report_number || '-'}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date Range</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
                                {report?.date_from && report?.date_to
                                    ? `${formatDate(report.date_from)} - ${formatDate(report.date_to)}`
                                    : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Submitted At</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 600, color: '#1e293b', mt: 0.5 }}>{formatDate(report?.submitted_at)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Approved At</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 600, color: '#1e293b', mt: 0.5 }}>{formatDate(report?.approved_at)}</Typography>
                        </Grid>
                        {report?.description && (
                            <Grid item xs={12}>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', mt: 0.5 }}>{report.description}</Typography>
                            </Grid>
                        )}
                        {report?.rejected_reason && (
                            <Grid item xs={12}>
                                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily }}>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>Rejection Reason</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{report.rejected_reason}</Typography>
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                    {report?.status === 'DRAFT' && (
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={actionLoading}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                        >
                            {actionLoading ? 'Submitting...' : 'Submit for Approval'}
                        </Button>
                    )}
                    {report?.status === 'SUBMITTED' && (
                        <>
                            <Button
                                variant="contained"
                                startIcon={<CheckCircleIcon />}
                                onClick={handleApprove}
                                disabled={actionLoading}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                {actionLoading ? 'Approving...' : 'Approve'}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<CancelIcon />}
                                onClick={() => setRejectOpen(true)}
                                disabled={actionLoading}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                </Box>

                {/* Expenses Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                            Expenses ({expenses.length})
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Expense #', 'Date', 'Merchant', 'Category', 'Amount', 'Status'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {expenses.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No expenses in this report yet</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : expenses.map(exp => {
                                    const expStatus = STATUS_COLORS[exp.status] || STATUS_COLORS.DRAFT;
                                    return (
                                        <TableRow key={exp.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {exp.expense_number || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(exp.expense_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {exp.merchant_name || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {exp.category_name || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {formatCurrency(exp.total_amount || exp.amount)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={exp.status?.replace(/_/g, ' ') || 'DRAFT'}
                                                    size="small"
                                                    sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: expStatus.bg, color: expStatus.color }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>

            {/* Reject Dialog */}
            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Reject Expense Report</DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Rejection Reason"
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        multiline
                        rows={3}
                        required
                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setRejectOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleReject}
                        disabled={actionLoading || !rejectReason.trim()}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {actionLoading ? 'Rejecting...' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
